import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { getCdnWAFWhitelistColumns } from '@constants';
import { FormListLayout } from '@containers';
import { ApiEndpoint, FormActionType, FormType, ServiceModalName, StatusType, Translation } from '@enums';
import { useAppDispatch, useFetchFormList, useFormHandlers, useFormList, useFormSubmissionStatus } from '@hooks';
import { addServiceModal, deleteFormContent } from '@store';

import { WAFWhiteListModal } from '../../subComponents';

export const WhiteListWAFForm = () => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnWAF;
  const endpoint = ApiEndpoint.WHITELIST;

  const { t: tTabs } = useTranslation(Translation.Configuration, { keyPrefix: 'tabs.waf' });
  const { t: tButtons } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons.waf' });
  const { t: tTables } = useTranslation(Translation.Description, { keyPrefix: 'configuration.tables' });
  const { t: tModal } = useTranslation(Translation.Configuration, { keyPrefix: 'modals' });
  const { t: tWafModal } = useTranslation(Translation.Configuration, { keyPrefix: 'modals.waf' });
  const { t: tCommon } = useTranslation(Translation.Common);

  const [addWafWhiteListModal, setAddWafWhiteListModal] = useState<boolean>(false);

  const isWafWhiteListSubmitting = useFormSubmissionStatus({ formType });

  useFetchFormList({
    formType,
    params: { application_id },
    globalOptions: {
      endpoint,
      pagination: true,
    },
    hookOptions: {
      resetFormTrigger: false,
    },
  });

  useFetchFormList({
    formType,
    params: { application_id, page_size: 100 },
    globalOptions: {
      endpoint: ApiEndpoint.RULE,
    },
    hookOptions: {
      resetFormTrigger: false,
    },
  });

  const ruleList = useFormList({ formType, endpoint: ApiEndpoint.RULE });

  const ruleOptions = ruleList.map(({ id, rule_name }) => ({
    value: id,
    label: rule_name,
  }));

  const wafWhitelistTableColumns = useMemo(
    () => getCdnWAFWhitelistColumns({ options: ruleOptions }),
    [tTables, ruleOptions],
  );
  const wafWhiteListData = useFormList({ formType, endpoint });

  const whiteListFormHandlers = useFormHandlers({
    formType,
    handlerOptions: [
      {
        type: FormActionType.Create,
        function: () => setAddWafWhiteListModal(true),
        label: tButtons('addNewWhiteList'),
      },
      {
        type: FormActionType.Edit,
        function: () => setAddWafWhiteListModal(true),
      },
      {
        type: FormActionType.Delete,
        function: (rowData) => {
          dispatch(
            addServiceModal({
              name: ServiceModalName.ConfirmModal,
              payload: {
                label: {
                  title: tWafModal('wafWhiteListDelete'),
                  text: tModal('areYouSureDelete', { domain: rowData?.name }),
                  confirmButton: tCommon('button.delete'),
                },
                option: {
                  modalType: StatusType.Delete,
                  button: {
                    loading: isWafWhiteListSubmitting,
                  },
                },
                callbackFunction: {
                  submit: () =>
                    dispatch(
                      deleteFormContent({
                        formType,
                        params: {
                          content_id: rowData?.id,
                        },
                        globalOptions: {
                          refetch_id: application_id,
                          endpoint,
                        },
                      }),
                    ),
                },
              },
            }),
          );
        },
      },
    ],
  });

  return (
    <FormListLayout
      label={{
        title: tTabs('whiteList'),
      }}
      tableData={{
        columns: wafWhitelistTableColumns,
        actionHandlers: whiteListFormHandlers,
        hasChildren: true,
        formType,
        endpoint,
        customTable: wafWhiteListData,
      }}
    >
      {addWafWhiteListModal && (
        <WAFWhiteListModal open={addWafWhiteListModal} onClose={() => setAddWafWhiteListModal(false)} />
      )}
    </FormListLayout>
  );
};
